/**
 * chartMapper.ts — Pure translator from pipeline visualization metadata to Plotly objects.
 * v3.59 - 2026-03-02 - Fix: indicator/bullet ?? 0 → ?? null to distinguish real $0 KPI from missing data (ADR-002 §15.1)
 * v3.58 - 2026-02-26 - Fix: indicator/bullet use columnRoles.value instead of Object.values() scan (AR aging "0" bug)
 * v3.57 - 2026-02-26 - Fix: coerce string-encoded numbers (DuckDB/Pandas) to prevent table fallback
 * v3.56 - 2026-02-25 - Use viz.columnRoles for explicit axis mapping + safety net fallback (ADR-002)
 * v3.47 - 2026-02-24 - Story 9B fix: skipSort option so popup sort toggle is respected (ADR-002)
 * v3.43 - 2026-02-24 - Story 8E: Horizontal bar guards on viz-aware path, zero filter on bar types (ADR-002)
 * v3.42 - 2026-02-24 - Universal null filter, resolveColors, resolveDataLabels, colorPalettes.ts (ADR-002)
 * v3.41 - 2026-02-24 - Story 8C Issue 1: horizontal_bar margin.l=200 + suppress yaxis title (ADR-002)
 * v3.38 - 2026-02-23 - Story 8: Dark mode contrast, column validation, grid lines (ADR-002)
 * v3.37 - 2026-02-23 - Story 7B: Visual fixes (Issues 1,2,4,5,6) (ADR-002)
 * v3.35 - 2026-02-23 - Story 5: chartMapper.ts (ADR-002)
 *
 * ZERO chart-selection intelligence. All decisions (chart type, colors, labels)
 * are pre-computed by the pipeline. This file is a translator, not a decision-maker.
 *
 * NOTE on horizontal bar axis mapping: In Plotly horizontal bars, the VALUE axis
 * is X (horizontal) and the CATEGORY axis is Y (vertical). So:
 *   Plotly x array = numeric measure values (report.yColumn)
 *   Plotly y array = category labels (report.xColumn)
 */
// v3.55 - Story 12A.1: Import from chartUtils (was ../services/api) for chart-renderer compatibility
import type { ChartReportData, VisualizationMetadata } from './chartUtils';
import { PALETTES } from './colorPalettes';

export interface ChartMapperResult {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  useTable: boolean;
}

// v3.37 - Truncate long category labels for chart readability
const truncateLabel = (label: string, max = 40): string =>
  label.length > max ? label.substring(0, max - 1) + '\u2026' : label;

function col(data: Record<string, any>[], key?: string): any[] {
  return key ? data.map((r) => r[key]) : [];
}

// v3.57 - Coerce value to number: handles string-encoded numbers from DuckDB/Pandas
// serialization (e.g., "123.45" → 123.45). Returns null for non-numeric values.
function asNum(v: any): number | null {
  if (typeof v === 'number') return isNaN(v) ? null : v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return isNaN(n) ? null : n;
  }
  return null;
}

// v3.57 - Extract numeric column values with coercion (string "42" → number 42).
// Used for Plotly trace values so chart renders even when pipeline sends string-encoded numbers.
// Non-numeric values default to 0 (safe for bar/pie/treemap where missing = zero).
function numCol(data: Record<string, any>[], key?: string): number[] {
  if (!key) return [];
  return data.map((r) => asNum(r[key]) ?? 0);
}

// v3.57 - Nullable variant: non-numeric → null (Plotly omits the point).
// Use for scatter/line where 0 would be a phantom point at the origin.
function numColNullable(data: Record<string, any>[], key?: string): (number | null)[] {
  if (!key) return [];
  return data.map((r) => asNum(r[key]));
}

// --- v3.42 - Universal color resolution (runs for every chart, every path) ---

/**
 * Resolve marker colors for a set of numeric values.
 * Priority: (1) viz paletteType=financial_polarity, (2) mixed +/- data polarity,
 * (3) viz palette, (4) qualitative fallback.
 * Returns a single color string OR a per-bar color array.
 */
function resolveColors(
  values: (number | null | undefined)[],
  viz: VisualizationMetadata | null | undefined,
  theme: 'light' | 'dark'
): string | string[] {
  const fp = PALETTES.financialPolarity[theme];

  // (a) Pipeline explicitly tagged as financial polarity
  if (viz?.colorScheme?.paletteType === 'financial_polarity') {
    return values.map((v) => (v == null || v === 0) ? fp.neutral : v > 0 ? fp.positive : fp.negative);
  }

  // (b) Data-driven: mixed positive and negative → apply financial polarity as safety net
  const nums = values.filter((v): v is number => typeof v === 'number' && v !== 0);
  const hasPositive = nums.some((v) => v > 0);
  const hasNegative = nums.some((v) => v < 0);
  if (hasPositive && hasNegative) {
    return values.map((v) => (v == null || v === 0) ? fp.neutral : v > 0 ? fp.positive : fp.negative);
  }

  // (c) Pipeline palette or qualitative fallback (single color for uniform-sign data)
  if (viz?.colorScheme) {
    const pal = theme === 'dark' && viz.colorScheme.darkModeOverride
      ? viz.colorScheme.darkModeOverride
      : viz.colorScheme.palette;
    if (pal && pal.length > 0) return pal[0];
  }
  return PALETTES.qualitative[theme][0];
}

/**
 * Resolve a full palette array for multi-series charts (grouped bar, stacked, donut, etc.).
 * Same priority as resolveColors but returns the array, not per-bar mapping.
 */
function resolvePalette(
  viz: VisualizationMetadata | null | undefined,
  theme: 'light' | 'dark'
): string[] {
  if (viz?.colorScheme) {
    if (theme === 'dark' && viz.colorScheme.darkModeOverride) return viz.colorScheme.darkModeOverride;
    if (viz.colorScheme.palette && viz.colorScheme.palette.length > 0) return viz.colorScheme.palette;
  }
  return PALETTES.qualitative[theme];
}

/**
 * Resolve data label text array for double encoding (Knowledge Map §3 colorblind rule).
 * If data has mixed +/- values: prefix with + or - so meaning survives without color.
 * Otherwise returns undefined (no data labels needed).
 */
function resolveDataLabels(
  values: (number | null | undefined)[]
): string[] | undefined {
  const nums = values.filter((v): v is number => typeof v === 'number' && v !== 0);
  const hasPositive = nums.some((v) => v > 0);
  const hasNegative = nums.some((v) => v < 0);
  if (!hasPositive || !hasNegative) return undefined;

  return values.map((v) => {
    if (v == null || v === 0) return '0';
    return v > 0 ? `+${v.toLocaleString()}` : v.toLocaleString(); // negative already has minus
  });
}

// --- Layout helpers ---

// v3.37 - Issue 2: Removed title from layout. PlotlyChart.tsx owns the title display.
// v3.38 - Added grid lines for dark mode (#333333)
function buildLayout(report: ChartReportData, viz: VisualizationMetadata | null | undefined, theme: 'light' | 'dark'): Partial<Plotly.Layout> {
  const dark = theme === 'dark';
  return {
    xaxis: {
      title: { text: viz?.labels?.xAxis },
      gridcolor: dark ? '#333333' : '#E0E0E0',
      zerolinecolor: dark ? '#444444' : '#CCCCCC',
    },
    yaxis: {
      title: { text: viz?.labels?.yAxis },
      gridcolor: dark ? '#333333' : '#E0E0E0',
      zerolinecolor: dark ? '#444444' : '#CCCCCC',
    },
    showlegend: viz?.displayRules?.showLegend ?? false,
    paper_bgcolor: dark ? '#121212' : '#FFFFFF',
    plot_bgcolor: dark ? '#1E1E1E' : '#FFFFFF',
    font: { color: dark ? '#E0E0E0' : '#212121' },
    margin: { l: 60, r: 20, t: 10, b: 40 },
  };
}

// v3.38 - Part B: Check for mixed types in numeric columns
function hasMixedNumericTypes(data: Record<string, any>[], colName: string): boolean {
  if (!colName || data.length === 0) return false;
  let hasNumber = false;
  let hasNonNumber = false;
  for (let i = 0; i < Math.min(data.length, 50); i++) {
    const val = data[i][colName];
    if (val === null || val === undefined) continue;
    if (typeof val === 'number') hasNumber = true;
    else hasNonNumber = true;
    if (hasNumber && hasNonNumber) return true;
  }
  return false;
}

// --- Fallback path ---

/**
 * Static fallback (THE production path today — most reports have visualization: null).
 * Horizontal bar: Plotly x = measure (report.yColumn), Plotly y = category (report.xColumn).
 * If pipeline assigned both, trust them. Otherwise scan col_metadata for first string/number.
 * If both columns are numeric (no string column exists), fall through to table view.
 */
function buildFallback(report: ChartReportData, filteredRows: Record<string, any>[], theme: 'light' | 'dark', options?: ChartMapperOptions): ChartMapperResult {
  // Start with pipeline-assigned roles (may be null)
  let xKey = report.yColumn; // measure -> Plotly horizontal x
  let yKey = report.xColumn; // category -> Plotly horizontal y

  // Fill gaps from column metadata
  if (!xKey || !yKey) {
    const strCol = report.columns.find((c) => c.type === 'string');
    const numCol = report.columns.find((c) => c.type === 'number');
    if (!yKey) yKey = strCol?.name ?? report.xColumn; // prefer string; fall back to pipeline xColumn even if numeric
    if (!xKey) xKey = numCol?.name;
  }

  // If we still can't determine axes, render as table
  if (!xKey || !yKey) return { data: [], layout: {}, useTable: true };

  // v3.38 - Part B: Validate columns exist in data
  if (filteredRows.length > 0) {
    const firstRow = filteredRows[0];
    if (!firstRow.hasOwnProperty(xKey) || !firstRow.hasOwnProperty(yKey)) {
      return { data: [], layout: {}, useTable: true };
    }
  }

  // v3.37 - Issue 1: If both axes are numeric, render as table (not a broken bar chart)
  const xType = report.columns.find((c) => c.name === yKey)?.type; // yKey = Plotly y = category axis
  const yType = report.columns.find((c) => c.name === xKey)?.type; // xKey = Plotly x = value axis
  if (xType === 'number' && yType === 'number') {
    return { data: [], layout: {}, useTable: true };
  }

  // v3.38 - Part B: Mixed types in the numeric column → table fallback
  if (hasMixedNumericTypes(filteredRows, xKey)) {
    return { data: [], layout: {}, useTable: true };
  }

  // v3.37 - Issue 5: Filter zero-value rows and cap at 20 categories
  // v3.57 - asNum() coerces string-encoded numbers from DuckDB/Pandas
  let chartData = filteredRows.filter((row) => {
    const n = asNum(row[xKey!]);
    return n !== null && n !== 0;
  });
  if (!options?.skipSort) chartData.sort((a, b) => (asNum(b[xKey!]) ?? 0) - (asNum(a[xKey!]) ?? 0)); // v3.47 - skipSort
  if (chartData.length > 20) {
    chartData = chartData.slice(0, 20);
  }

  // If all rows were zero, fall through to table
  if (chartData.length === 0) return { data: [], layout: {}, useTable: true };

  // v3.43 - Truncate at 30 chars, dynamic margin 120-250px, hovertext with full label
  const categoryLabels = chartData.map((row) => truncateLabel(String(row[yKey!] ?? ''), 30));
  const fullLabels = chartData.map((row) => String(row[yKey!] ?? ''));
  const maxLabelLength = Math.max(...categoryLabels.map((l) => l.length));
  const leftMargin = Math.max(120, Math.min(250, maxLabelLength * 7));

  const layout = buildLayout(report, null, theme);
  layout.margin = { l: leftMargin, r: 20, t: 10, b: 40 };
  layout.yaxis = { ...layout.yaxis, title: { text: '' }, automargin: true };

  // v3.42 - Universal color + data label resolution
  // v3.57 - numCol() coerces string-encoded numbers so Plotly + resolveColors get real numbers
  const xValues = numCol(chartData, xKey);
  const colors = resolveColors(xValues, null, theme);
  const labels = resolveDataLabels(xValues);

  const trace: any = {
    type: 'bar',
    orientation: 'h',
    x: xValues,
    y: categoryLabels,
    marker: { color: colors },
    hovertext: fullLabels,
    hoverinfo: 'x+text',
  };
  if (labels) {
    trace.text = labels;
    trace.textposition = 'outside';
  }

  return { data: [trace], layout, useTable: false };
}

// --- Multi-series helper ---

function groupTraces(report: ChartReportData, filteredRows: Record<string, any>[], type: 'bar' | 'scatter', palette: string[], mode?: string): Plotly.Data[] {
  const groups = Array.from(new Set(col(filteredRows, report.groupColumn))).slice(0, 4);
  // v3.57 - scatter (line charts) uses numColNullable to omit points; bar uses numCol (0 = zero-height bar)
  const yCol = type === 'scatter' ? numColNullable : numCol;
  return groups.map((g, i) => {
    const rows = filteredRows.filter((r) => r[report.groupColumn!] === g);
    return { type, mode, name: String(g), x: col(rows, report.xColumn), y: yCol(rows, report.yColumn), marker: { color: palette[i % palette.length] } } as Plotly.Data;
  });
}

// --- Main entry point ---

// v3.47 - Story 9B fix: skipSort lets caller control sort order (popup sort toggle)
export interface ChartMapperOptions { skipSort?: boolean; }

export function mapReportToPlotly(report: ChartReportData, viz: VisualizationMetadata | null | undefined, theme: 'light' | 'dark', options?: ChartMapperOptions): ChartMapperResult {
  // v1.64 - If viz has columnRoles, use them for explicit axis mapping (no guessing).
  // Pipeline injects validated column names so chartMapper never has to infer roles.
  if (viz?.columnRoles?.category && viz?.columnRoles?.value) {
    report.xColumn = viz.columnRoles.category;
    report.yColumn = viz.columnRoles.value;
    if (viz.columnRoles.series) report.groupColumn = viz.columnRoles.series;
  }

  // v3.42 - Task 1: Universal null filter — remove rows where xColumn or yColumn is null/undefined.
  // Applies to ALL paths. "View as table" still shows originals — only chart visualization filters.
  const filteredRows = report.data.filter((row) =>
    (report.xColumn ? row[report.xColumn] != null : true) &&
    (report.yColumn ? row[report.yColumn] != null : true)
  );
  if (filteredRows.length === 0) return { data: [], layout: {}, useTable: true };

  // Fallback path — no viz metadata
  if (!viz?.chartType) return buildFallback(report, filteredRows, theme, options);

  // v3.38 - Part B: Validate columns exist before building traces
  if (filteredRows.length > 0) {
    const firstRow = filteredRows[0];
    if (report.xColumn && !firstRow.hasOwnProperty(report.xColumn)) return { data: [], layout: {}, useTable: true };
    if (report.yColumn && !firstRow.hasOwnProperty(report.yColumn)) return { data: [], layout: {}, useTable: true };
  }

  // v3.42 - Universal color + label resolution (runs for every chart type)
  // v3.57 - numCol() ensures resolveColors gets actual numbers, not string-encoded "42"
  const valueCol = report.yColumn;
  const numericValues = valueCol ? numCol(filteredRows, valueCol) : [];
  const colors = resolveColors(numericValues, viz, theme);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dataLabels = resolveDataLabels(numericValues);
  const palette = resolvePalette(viz, theme);

  const layout = buildLayout(report, viz, theme);
  let data: Plotly.Data[] = [];

  switch (viz.chartType) {
    // v3.43 - Story 8E: Same guards as fallback — zero filter, sort, cap 20, truncate, dynamic margin
    case 'horizontal_bar': {
      const hbMeasure = report.yColumn;
      const hbCat = report.xColumn;
      // v3.57 - asNum() coerces string-encoded numbers ("123" → 123) from DuckDB/Pandas
      let hbRows = hbMeasure
        ? filteredRows.filter((r) => { const n = asNum(r[hbMeasure]); return n !== null && n !== 0; })
        : filteredRows;
      if (!options?.skipSort) hbRows.sort((a, b) => Math.abs(asNum(b[hbMeasure!]) ?? 0) - Math.abs(asNum(a[hbMeasure!]) ?? 0)); // v3.47 - skipSort
      if (hbRows.length > 20) hbRows = hbRows.slice(0, 20);
      if (hbRows.length === 0) return { data: [], layout: {}, useTable: true };
      const hbLabels = hbCat ? hbRows.map((r) => truncateLabel(String(r[hbCat] ?? ''), 30)) : [];
      const hbFull = hbCat ? hbRows.map((r) => String(r[hbCat] ?? '')) : undefined;
      const hbMaxLen = Math.max(...hbLabels.map((l) => l.length), 0);
      const hbMargin = Math.max(120, Math.min(250, hbMaxLen * 7));
      // v3.57 - numCol() ensures Plotly receives actual numbers, not string "42"
      const hbValues = hbMeasure ? numCol(hbRows, hbMeasure) : [];
      const hbColors = resolveColors(hbValues, viz, theme);
      const hbDL = resolveDataLabels(hbValues);
      const trace: any = {
        type: 'bar', orientation: 'h', x: hbValues, y: hbLabels,
        marker: { color: hbColors }, hovertext: hbFull, hoverinfo: hbFull ? 'x+text' : undefined,
      };
      if (hbDL) { trace.text = hbDL; trace.textposition = 'outside'; }
      data = [trace];
      layout.margin = { ...layout.margin, l: hbMargin };
      layout.yaxis = { ...layout.yaxis, title: { text: '' }, automargin: true };
      break;
    }
    // v3.43 - Story 8E: Zero filter for grouped/stacked bar types
    case 'grouped_bar': {
      // v3.57 - asNum() coerces string-encoded numbers
      const gbRows = report.yColumn
        ? filteredRows.filter((r) => { const n = asNum(r[report.yColumn!]); return n !== null && n !== 0; })
        : filteredRows;
      data = report.groupColumn ? groupTraces(report, gbRows, 'bar', palette) : [{ type: 'bar', x: col(gbRows, report.xColumn), y: numCol(gbRows, report.yColumn), marker: { color: colors } }];
      (layout as any).barmode = 'group';
      break;
    }
    case 'stacked_bar': {
      // v3.57 - asNum() coerces string-encoded numbers
      const sbRows = report.yColumn
        ? filteredRows.filter((r) => { const n = asNum(r[report.yColumn!]); return n !== null && n !== 0; })
        : filteredRows;
      data = report.groupColumn ? groupTraces(report, sbRows, 'bar', palette) : [{ type: 'bar', x: col(sbRows, report.xColumn), y: numCol(sbRows, report.yColumn), marker: { color: colors } }];
      (layout as any).barmode = 'stack';
      break;
    }
    case 'line':
      // v3.57 - numColNullable() for y-axis: null omits the point instead of plotting at zero
      data = report.groupColumn
        ? groupTraces(report, filteredRows, 'scatter', palette, 'lines+markers')
        : [{ type: 'scatter', mode: 'lines+markers', x: col(filteredRows, report.xColumn), y: numColNullable(filteredRows, report.yColumn), line: { color: typeof colors === 'string' ? colors : colors[0] } }];
      break;
    case 'donut':
      // v3.57 - numCol() for values ensures string-encoded numbers are coerced
      data = [{ type: 'pie', hole: 0.4, values: numCol(filteredRows, report.yColumn), labels: col(filteredRows, report.xColumn), marker: { colors: palette } }];
      break;
    case 'heatmap':
      // v3.57 - asNum() coerces string-encoded numbers in heatmap z-matrix
      data = [{ type: 'heatmap', z: filteredRows.map((r) => Object.values(r).map((v) => asNum(v)).filter((v): v is number => v !== null)) }];
      break;
    case 'indicator': {
      // v3.58 - Fix: use columnRoles.value for explicit column binding instead of Object.values() scan.
      // Object.values() picks the first numeric value in JS insertion order, which may be 0 from
      // an unrelated column (e.g. a count or boolean) that appears before the actual value column.
      // v3.59 - Fix: ?? undefined instead of ?? 0 so Plotly distinguishes real $0 KPI from missing data (ADR-002 §15.1) - 2026-03-02
      const valKey = viz?.columnRoles?.value || report.yColumn;
      const val = filteredRows[0]
        ? (valKey ? asNum(filteredRows[0][valKey]) : null) ?? asNum(Object.values(filteredRows[0]).find((v) => asNum(v) !== null)) ?? undefined
        : undefined;
      data = [{ type: 'indicator', mode: 'number+delta', value: val }];
      break;
    }
    case 'bullet': {
      // v3.58 - Fix: use columnRoles.value (same as indicator fix above)
      // v3.59 - Fix: ?? undefined instead of ?? 0 (same as indicator fix above) - 2026-03-02
      const bValKey = viz?.columnRoles?.value || report.yColumn;
      const bVal = filteredRows[0]
        ? (bValKey ? asNum(filteredRows[0][bValKey]) : null) ?? asNum(Object.values(filteredRows[0]).find((v) => asNum(v) !== null)) ?? undefined
        : undefined;
      data = [{ type: 'indicator', mode: 'gauge+number', value: bVal, gauge: { shape: 'bullet' } }];
      break;
    }
    case 'scatter_outlier':
      // v3.57 - numColNullable() for scatter: null omits the point instead of phantom at origin
      data = [{ type: 'scatter', mode: 'markers', x: numColNullable(filteredRows, report.xColumn), y: numColNullable(filteredRows, report.yColumn), marker: { color: colors } }];
      break;
    case 'waterfall':
      // v3.57 - numCol() for y-values
      data = [{ type: 'waterfall', x: col(filteredRows, report.xColumn), y: numCol(filteredRows, report.yColumn) }];
      break;
    case 'treemap':
      // v3.57 - numCol() for values
      data = [{ type: 'treemap', labels: col(filteredRows, report.xColumn), parents: filteredRows.map(() => ''), values: numCol(filteredRows, report.yColumn) }];
      break;
    case 'funnel':
      data = [{ type: 'funnel', x: col(filteredRows, report.yColumn), y: col(filteredRows, report.xColumn) }];
      break;
    case 'table':
      return { data: [], layout: {}, useTable: true };
    default:
      return buildFallback(report, filteredRows, theme, options);
  }

  // v1.64 - Safety net: if viz-aware path produced traces with no data points, fall back
  // to the horizontal bar builder which auto-detects string + numeric columns.
  if (data.length > 0) {
    const hasPoints = data.some((d: any) => {
      if (d.x?.length > 0 || d.y?.length > 0) return true;
      if (d.values?.length > 0) return true; // pie/donut
      if (d.value !== undefined) return true; // indicator
      if (d.z?.length > 0) return true; // heatmap
      if (d.labels?.length > 0) return true; // treemap
      return false;
    });
    if (!hasPoints) return buildFallback(report, filteredRows, theme, options);
  }

  return { data, layout, useTable: false };
}
